import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { CheckCircle, Star, Send, User } from "lucide-react";
import FeatureHeader from "../components/FeatureHeader";
import Loader from "../components/loader";

const styles = {
  addToCartButton: {
    flex: 2,
    padding: '16px',
    backgroundColor: 'var(--primary)',
    color: 'white',
    border: 'none',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },

  commentInputWrapper: {
    marginBottom: 12,
  },

  commentTextareaModern: {
    width: "100%",
    padding: "12px 16px",
    borderRadius: "12px",
    border: "2px solid #E5E7EB",
    fontSize: 15,
    lineHeight: 1.6,
    color: "#333",
    resize: "vertical",
    outline: "none",
    transition: "border-color 0.2s ease",
    background: "#FAFAFA",
  },

  commentActions: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
  },

  ratingStars: {
    display: "flex",
    alignItems: "center",
    gap: 2,
  },

  commentSubmitButton: {
    padding: "8px 16px",
    backgroundColor: "var(--primary)",
    color: "white",
    border: "none",
    borderRadius: "10px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    minWidth: "120px",
  },

  commentSubmitBtnContent: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  commentsList: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },

  noReviews: {
    textAlign: "center",
    color: "#999",
    fontSize: 14,
    padding: "20px 0",
  },

  reviewItem: {
    background: "#F9FAFB",
    borderRadius: "12px",
    padding: 12,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },

  commentHeader: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 8,
  },

  commentUser: {
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    margin: 0,
  },

  commentStarRating: {
    display: "flex",
    gap: 2,
    marginLeft: "auto",
  },

  commentDate: {
    fontSize: 11,
    color: "#999",
  },

  commentText: {
    fontSize: 14,
    lineHeight: 1.6,
    color: "#444",
    margin: 0,
  },
};

export default function LabTestProfile() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [newRating, setNewRating] = useState(0);
   const [submittingComment, setSubmittingComment] = useState(false);
  const [activeTab, setActiveTab] = useState("description");

  const handleMouseEnter = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.35)";
  }, []);

  const handleMouseLeave = useCallback((e) => {
    e.currentTarget.style.background = "rgba(255,255,255,0.2)";
  }, []);

  useEffect(() => {
    const fetchTest = async () => {
      try {
        const res = await api.get(`/api/labtests/${testId}`);
        setTest(res.data.test);
        console.log(res.data, "test information")
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [testId]);

  const handleAddToCart = () => {
    if (!test) return;
    setAdding(true);

    const savedCart = JSON.parse(localStorage.getItem("labCart")) || [];
    const exists = savedCart.find((item) => item.id === test._id);

    const updatedCart = exists
      ? savedCart.map((item) =>
          item.id === test._id ? { ...item, qty: item.qty + 1 } : item
        )
      : [
          ...savedCart,
          {
            id: test._id,
            title: test.name,
            price: test.price,
            image: test.image,
            category: test.category,
            qty: 1,
          },
        ];

    localStorage.setItem("labCart", JSON.stringify(updatedCart));

    setTimeout(() => {
      setAdding(false);
      navigate("/lab-cart");
    }, 700);
  };

  const handlePostReview = async () => {
    if (!newComment.trim()) return;
    try {
      setSubmittingComment(true);
      const userId = localStorage.getItem("userId");
      const userName = localStorage.getItem("userName") || "Anonymous";
      await api.post(`/api/labtests/${testId}/reviews`, {
        userId,
        userName,
        comment: newComment,
        rating: newRating,
      });
      const res = await api.get(`/api/labtests/${testId}`);
      setTest(res.data.test);
      setNewComment("");
      setNewRating(0);
    } catch (err) {
      console.error(err);
    } finally {
      setSubmittingComment(false);
    }
  };

  if (loading)
    return (
        <Loader />
    );

  if (!test)
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        Test not found
      </div>
    );

return (
  <div
  className = "container"
    style={{
      width: "100vw",
      minHeight: "100vh",
      background: "var(--bg)",
      paddingBottom: 0, // space for CTA
      overflowX: "hidden",
      fontFamily: "Inter, system-ui, sans-serif",
    }}
  >
    {/* HEADER */}
   
   <FeatureHeader title = "Product Details" subtitle = "Check the details of the product" onBack = {()=> navigate(-1)}/>

    {/* HERO IMAGE */}
    {test.image && (
      <div
        style={{
          width: "100%",
          height: 300,
          
          overflow: "hidden",
          background : "white",
          borderRadius : "24px"
        }}
      >
        <img
          src={test.image}
          alt={test.name}
          style={{
            width: "80%",
            height: "90%",
            marginTop : "5%",
            marginLeft : "10%",
            alignItem : "center",
            justifyContent : "center",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
    )}

    {/* NAME */}
    <div
      style={{
        padding: "20px 16px",
        fontSize: 18,
        fontWeight: "bold",
        color: "#333",
      }}
    >
      {test.name}
    </div>

    <div style = {{borderRadius : "15px", background : "white", paddingBottom : "15px"}}>
      <div style = {{display : "flex", width : "100%", justifyContent : "space-between", alignItems : "center"}}>
      <h6 style = {{
        fontSize : "25px",
        fontWeight : "bold",
        color : "var(--primary)",
        padding : "20px 16px"
      }}>${test.price}</h6>

      <h6 style = {{
        fontSize : "20px",
        fontWeight : "bold",
        color : "var(--primary)",
        padding : "20px 16px"
      }}>
         {test.category}
      </h6>
      </div>
      <button 
      onClick={handleAddToCart}
      style= {{
        flex: 2,
    padding: '16px',
    backgroundColor: 'var(--primary)',
    color: 'white',
    width : "90%",
    marginLeft : "5%",
    border: 'none',
    borderRadius: '16px',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
      }}
      >Add to Cart
      </button>

      

    </div>
      <br></br>
    {/* CONTENT */}
    <div style={{ padding: "20px 16px", background : "white", borderRadius : "16px" }}>
      
      <h6 style = {{color : "var(--primary)", textDecoration : "underlined", fontWeight : "bold", fontSize : 15}}>Test Description</h6>
      {/* DESCRIPTION */}
      {test.description && (
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.6,
            color: "#444",
            marginBottom: 20,
          }}
        >
          {test.description}
        </p>
      )}

      {/* INFO CARDS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr",
          gap: 12,
        }}
      >
        {[
          ["Laboratory", test.laboratory],
          ["Preparation", test.preparation],
          ["Sample Type", test.sampleType],
          ["Estimated Time", test.estimatedTime],
          ["Ratings", test.ratings && `${test.ratings} ⭐`],
        ]
          .filter(([, v]) => v)
          .map(([label, value]) => (
            <div
              key={label}
              style={{
                background: "#fff",
                borderRadius: 14,
                padding: 14,
                boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  color: "#888",
                  marginBottom: 4,
                }}
              >
                {label}
              </div>
              <div
                style={{
                  fontSize: 15,
                  fontWeight: 600,
                  color: "#222",
                }}
              >
                {value}
              </div>
            </div>
           ))}
       </div>
     </div>

    {/* REVIEWS / COMMENTS SECTION */}
     <div style={{ background: "white", borderRadius: "16px", padding: "20px 16px", marginTop: 16 }}>
       <div style={{ display: "flex", gap: 20, marginBottom: 16, borderBottom: "2px solid #f0f0f0" }}>
         <button
           onClick={() => setActiveTab("description")}
           style={{
             padding: "8px 16px",
             border: "none",
             background: activeTab === "description" ? "var(--primary)" : "transparent",
             color: activeTab === "description" ? "white" : "#666",
             borderRadius: "8px",
             cursor: "pointer",
             fontSize: 14,
             fontWeight: 600,
           }}
         >
           Description
         </button>
         <button
           onClick={() => setActiveTab("reviews")}
           style={{
             padding: "8px 16px",
             border: "none",
             background: activeTab === "reviews" ? "var(--primary)" : "transparent",
             color: activeTab === "reviews" ? "white" : "#666",
             borderRadius: "8px",
             cursor: "pointer",
             fontSize: 14,
             fontWeight: 600,
           }}
         >
           Reviews ({test.reviews?.length || 0})
         </button>
       </div>

       {activeTab === "description" && test.description && (
         <p style={{ fontSize: 15, lineHeight: 1.6, color: "#444" }}>{test.description}</p>
       )}

       {activeTab === "reviews" && (
         <div>
           <div style={{ marginBottom: 24 }}>
             <h4 style={{ fontSize: 16, fontWeight: 600, color: "#333", marginBottom: 12 }}>Write a review</h4>
             <div style={styles.commentInputWrapper}>
               <textarea
                 style={styles.commentTextareaModern}
                 placeholder="Share your experience with this lab test..."
                 value={newComment}
                 onChange={(e) => setNewComment(e.target.value)}
                 rows={4}
               />
             </div>
             <div style={styles.commentActions}>
               <div style={styles.ratingStars}>
                 {[1, 2, 3, 4, 5].map((star) => (
                   <Star
                     key={star}
                     size={20}
                     fill={star <= newRating ? "#FFD700" : "#E5E7EB"}
                     color={star <= newRating ? "#FFD700" : "#E5E7EB"}
                     style={{ cursor: "pointer", margin: "0 2px", transition: "all 0.2s ease", transform: star <= newRating ? "scale(1.1)" : "scale(1)" }}
                     onClick={() => setNewRating(star)}
                   />
                 ))}
               </div>
               <button
                 style={{
                   ...styles.commentSubmitButton,
                   opacity: !newComment.trim() ? 0.5 : 1,
                 }}
                 disabled={submittingComment || !newComment.trim()}
                 onClick={handlePostReview}
               >
                 {submittingComment ? "Posting..." : (
                   <span style={styles.commentSubmitBtnContent}>
                     <Send size={16} /> Post Review
                   </span>
                 )}
               </button>
             </div>
           </div>

           <div style={styles.commentsList}>
             {test.reviews && test.reviews.length === 0 ? (
               <p style={styles.noReviews}>No reviews yet. Be the first to share your experience!</p>
             ) : (
               (test.reviews || []).map((review) => (
                 <div key={review._id || review.date} style={styles.reviewItem}>
                   <div style={styles.commentHeader}>
                     <User size={16} />
                     <span style={styles.commentUser}>{review.user?.fullName || review.user?.name || review.userName || "Anonymous"}</span>
                     {review.rating > 0 && (
                       <div style={styles.commentStarRating}>
                         {[...Array(5)].map((_, i) => (
                           <Star
                             key={i}
                             size={14}
                             fill={i < review.rating ? "#FFD700" : "#E5E7EB"}
                             color={i < review.rating ? "#FFD700" : "#E5E7EB"}
                           />
                         ))}
                       </div>
                     )}
                     <span style={styles.commentDate}>
                       {new Date(review.date).toLocaleDateString()}
                     </span>
                   </div>
                   {review.comment && (
                   <p style={styles.commentText}>{review.comment}</p>
                   )}
                 </div>
               ))
             )}
           </div>
         </div>
       )}
     </div>

     </div>
   );


}
